import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Alert, StyleSheet, Text, TextInput, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';

import { useAuth } from '../../../contexts/AuthContext';
import { useSampleData } from '../../../contexts/SampleDataContext';
import { GlassButton } from '../../../components/ui/GlassButton';
import { colors, radii, spacing, typography } from '../../../core/theme/tokens';
import type { AuthStackParamList } from '../../../core/navigation/types';
import { finishAuthNavigation } from '../../../core/navigation/navigationRef';
import { getDeviceId } from '../../../services/deviceIdService';
import { authService, toAuthTokens } from '../services/authService';
import { AuthBrandHeader } from '../components/AuthBrandHeader';
import { AuthModalFrame } from '../components/AuthModalFrame';

type Props = NativeStackScreenProps<AuthStackParamList, 'OtpVerify'>;

const RESEND_COOLDOWN_SEC = 30;

export function OtpVerifyScreen({ navigation, route }: Props) {
  const { login } = useAuth();
  const { exitSampleMode, markPreviewSeen } = useSampleData();
  const { phoneNumber, dialCode, localNumber } = route.params;
  const [otp, setOtp] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SEC);
  const [rateLimitHint, setRateLimitHint] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const handleSendOtp = async () => {
    if (cooldown > 0) return;
    setIsSending(true);
    try {
      const deviceId = await getDeviceId();
      const response = await authService.sendOTP(phoneNumber, deviceId);
      const hint =
        response.attemptsRemaining != null
          ? `${response.attemptsRemaining} attempt${response.attemptsRemaining === 1 ? '' : 's'} remaining today.`
          : null;
      setRateLimitHint(hint);
      setCooldown(RESEND_COOLDOWN_SEC);
      Alert.alert('Code sent', `Check your phone for the verification code.${hint ? ` ${hint}` : ''}`);
    } catch (error) {
      Alert.alert('Error', error instanceof Error ? error.message : 'Failed to send code');
    } finally {
      setIsSending(false);
    }
  };

  const handleVerify = async () => {
    const code = otp.replace(/\D/g, '');
    if (code.length < 4) {
      Alert.alert('Enter code', 'Type the verification code from your SMS.');
      return;
    }

    setIsVerifying(true);
    try {
      const deviceId = await getDeviceId();
      const response = await authService.verifyOTP(phoneNumber, code, deviceId);
      await login(toAuthTokens(response), response.user);
      exitSampleMode();
      await markPreviewSeen();
      finishAuthNavigation();
    } catch (error) {
      Alert.alert('Verification failed', error instanceof Error ? error.message : 'Invalid code');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <AuthModalFrame onDismiss={() => navigation.goBack()}>
      <AuthBrandHeader
        headline="Enter your code"
        subtitle={`Sent to ${dialCode} ${localNumber}`}
      />

      <View style={styles.form}>
        <TextInput
          style={styles.otpInput}
          value={otp}
          onChangeText={(text) => setOtp(text.replace(/\D/g, '').slice(0, 6))}
          placeholder="••••••"
          placeholderTextColor={colors.textMuted}
          keyboardType="number-pad"
          maxLength={6}
          autoFocus
          editable={!isVerifying}
        />
        <GlassButton
          variant="primary"
          fullWidth
          onPress={handleVerify}
          disabled={isVerifying || otp.length < 4}
        >
          {isVerifying ? 'Verifying…' : 'Verify & continue'}
        </GlassButton>
        {isVerifying ? <ActivityIndicator color={colors.accent} /> : null}
        <GlassButton
          variant="secondary"
          fullWidth
          onPress={handleSendOtp}
          disabled={isSending || cooldown > 0}
        >
          {isSending ? 'Sending…' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
        </GlassButton>
        {rateLimitHint ? <Text style={styles.hint}>{rateLimitHint}</Text> : null}
      </View>

      <GlassButton variant="ghost" fullWidth onPress={() => navigation.goBack()}>
        Change phone number
      </GlassButton>
    </AuthModalFrame>
  );
}

const styles = StyleSheet.create({
  form: {
    gap: spacing.md,
  },
  otpInput: {
    ...typography.display,
    fontSize: 32,
    color: colors.text,
    letterSpacing: 12,
    textAlign: 'center',
    paddingVertical: spacing.lg,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.12)',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    fontVariant: ['tabular-nums'],
    minHeight: 72,
    outlineStyle: 'none',
  } as object,
  hint: {
    ...typography.caption,
    color: colors.textMuted,
    textAlign: 'center',
  },
});
